/* ==========================================================================
   Eco Traders — PDP Safe JS (refactor v2)
   Purpose: Editor‑safe, idempotent PDP behaviour with a single bootstrap.
   File: assets/product-pro.safe.js
   --------------------------------------------------------------------------
   TABLE OF CONTENTS
   00) File header & conventions
   10) Core: globals, guards, utilities (SINGLE SOURCE OF TRUTH)
   20) Feature flags (toggle modules on/off)
   30) Module 01 — Tabs (HL-style; editor-aware & idempotent)
   40) Module 02 — Variant/price sync (price, discount, fulfilment hook)
   50) Module 03 — retired (see bn-pdp-fulfilment.js)
   60) Module 04 — Thumbnails fallback (swap large media)
   70) Module 05 — Zoom overlay (PSWP adapter + fallback)
   80) Module 06 — Thumb-rail navigation (up/down, wheel, keyboard)
   85) Module 07 — Quantity stepper (min/max/step aware)
   90) Bootstrap & Editor lifecycle hooks (single initializer)
   99) Public API + tester-friendly aliases
   ========================================================================== */

(() => {
  "use strict";

/* =========================
 * 10) CORE: globals & utils
 * ======================= */

  const VERSION = "1.0.4";
  const IS_EDITOR = !!(window.Shopify && window.Shopify.designMode);

  // Global once-registry (guards global delegates from double-binding)
  const ONCE = (window.__ET_ONCE = window.__ET_ONCE || {});

  // DOM helpers (root can be a node or selector; scope-friendly $ / $$)
  const _norm = (root, sel) => {
    if (typeof root === "string" && sel && (sel.querySelector || sel.nodeType)) return [sel, root];
    if (root && !root.querySelector && typeof root.length === "number") root = root[0];
    return [root || document, sel];
  };
  const $  = (root, sel) => { const [r, s] = _norm(root, sel); return s ? r.querySelector(s) : r; };
  const $$ = (root, sel) => { const [r, s] = _norm(root, sel); return Array.from(r.querySelectorAll(s)); };
  const on = (el, evt, fn, opts) => el && el.addEventListener(evt, fn, opts);

  // CSS.escape polyfill (older Safari)
  if (!window.CSS) window.CSS = {};
  if (typeof window.CSS.escape !== "function") {
    window.CSS.escape = (s) => String(s).replace(/[^a-zA-Z0-9_\-]/g, m => "\\" + m);
  }

  // Idempotency markers (use data-* to survive editor DOM churn)
  const markReady = (node, key) => { if (node) node.dataset[key] = "1"; };
  const isReady   = (node, key) => node?.dataset?.[key] === "1";

  // One-shot per-element guard (for non-DOM nodes or when you don’t want attributes)
  const markOnce = (el, key) => {
    const k = "__etOnce:" + key;
    if (el[k]) return true;
    el[k] = 1; return false;
  };

  // Money formatting
  const formatMoney = (cents, fmt) => {
    try {
      if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, fmt || Shopify.money_format);
      }
    } catch(_) {}
    const v = (Number(cents)||0)/100;
    return new Intl.NumberFormat("en-AU",{style:"currency",currency:"AUD"}).format(v);
  };

  // Small attribute helper
  const setAttrIfDifferent = (el, name, val) => {
    if (!el) return;
    const v = String(val);
    if (el.getAttribute(name) !== v) el.setAttribute(name, v);
  };

/* =========================
 * 20) FEATURE FLAGS
 * ======================= */
  const FEATURES = {
    tabs: true,            // Module 01
    variantPrice: true,    // Module 02
    fulfilment: false,     // Module 03 retired — see assets/bn-pdp-fulfilment.js
    thumbsFallback: true,  // Module 04
    zoom: true,            // Module 05
    thumbNav: true,        // Module 06
    qty: true,             // Module 07
    quickAdd: false,       // (reserved for later)
    aiRecs: false          // (reserved for later)
  };

/* ========================================================================
 * 30) Module 01 — Tabs
 *  - ARIA-friendly tabs w/ roving tabindex + hash/session sync
 *  - Idempotent & editor-safe
 * ====================================================================== */
  function initTabs(scope = document) {
    $$(scope, ".pro-tabs").forEach((tabs) => {
      if (markOnce(tabs, "tabs")) return;

      const buttons = $$(tabs, ".pro-tabs__nav-btn, .pro-tabs__tab");
      let panels    = $$(tabs, ".pro-tabs__panel");
      if (!buttons.length) return;

      if (!panels.length) {
        // Panels can live outside .pro-tabs; resolve via aria-controls
        panels = buttons
          .map(b => document.getElementById(b.getAttribute("aria-controls") || b.dataset.tabTarget || ""))
          .filter(Boolean);
      }
      if (!panels.length) return;

      const pairs = buttons.map((btn) => {
        const id = btn.getAttribute("aria-controls") || btn.dataset.tabTarget || "";
        const panel = id && (tabs.querySelector("#" + CSS.escape(id)) || document.getElementById(id));
        return panel ? { btn, panel } : null;
      }).filter(Boolean);
      if (!pairs.length) return;

      const allButtons = pairs.map(p => p.btn);
      const allPanels  = pairs.map(p => p.panel);

      const storeKey = "et.pdp.tabs.active." + (tabs.getAttribute("data-section-id") || "");
      const slugify  = s => String(s||"").toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/(^-|-$)/g,"");

      function deactivateAll() {
        allButtons.forEach(b => {
          b.classList.remove("is-active");
          b.setAttribute("aria-selected","false");
          b.tabIndex = -1;
        });
        allPanels.forEach(p => { p.hidden = true; p.classList.remove("is-active"); });
      }

      function rebuildPairsIfDetached() {
        // If editor re-rendered panels, rebuild mapping on the fly
        if (pairs.some(p => !p.panel.isConnected)) {
          const rebuilt = buttons.map(b => {
            const id2 = b.getAttribute("aria-controls") || b.dataset.tabTarget || "";
            const p2  = id2 && (tabs.querySelector("#" + CSS.escape(id2)) || document.getElementById(id2));
            return p2 ? { btn: b, panel: p2 } : null;
          }).filter(Boolean);
          if (rebuilt.length) { pairs.length = 0; Array.prototype.push.apply(pairs, rebuilt); }
        }
      }

      function activate(idx, updateHash) {
        if (idx < 0 || idx >= pairs.length) return;
        rebuildPairsIfDetached();

        const { btn, panel } = pairs[idx];
        deactivateAll();

        btn.classList.add("is-active");
        btn.setAttribute("aria-selected","true");
        btn.tabIndex = 0;

        panel.hidden = false;
        panel.classList.add("is-active");

        try { sessionStorage.setItem(storeKey, String(idx)); } catch(_) {}
        if (updateHash && !IS_EDITOR) {
          try {
            const label = (btn.dataset.tabTitle || btn.textContent || "").trim();
            const url   = new URL(location.href);
            url.hash    = "tab=" + encodeURIComponent(slugify(label));
            history.replaceState(null, "", url);
          } catch(_) {}
        }
      }

      const indexFromHash = () => {
        if (IS_EDITOR) return -1;
        const m = (location.hash || "").match(/tab=([^&]+)/i);
        if (!m) return -1;
        const want = slugify(decodeURIComponent(m[1]));
        return allButtons.findIndex(b => slugify((b.dataset.tabTitle || b.textContent || "").trim()) === want);
      };

      // Delegated click
      const nav = tabs.querySelector(".pro-tabs__nav") || tabs;
      on(nav, "click", (ev) => {
        const btn = ev.target.closest(".pro-tabs__nav-btn, .pro-tabs__tab");
        if (!btn) return;
        const i = allButtons.indexOf(btn);
        if (i > -1) { ev.preventDefault(); activate(i, true); btn.focus(); }
      });

      // Keyboard (roving tabindex)
      allButtons.forEach((btn, i) => {
        on(btn, "keydown", (e) => {
          let next = null;
          switch (e.key) {
            case "ArrowRight":
            case "ArrowDown": next = (i + 1) % allButtons.length; break;
            case "ArrowLeft":
            case "ArrowUp":   next = (i - 1 + allButtons.length) % allButtons.length; break;
            case "Home": next = 0; break;
            case "End":  next = allButtons.length - 1; break;
            case "Enter":
            case " ":
              e.preventDefault(); activate(i, true); return;
            default: return;
          }
          e.preventDefault();
          allButtons[next].focus();
          activate(next, true);
        });
      });

      // Initial state: hash > session > first
      let initial = indexFromHash();
      if (initial < 0) {
        try { const saved = parseInt(sessionStorage.getItem(storeKey), 10); if (!isNaN(saved)) initial = saved; } catch(_) {}
      }
      if (initial < 0 || initial >= pairs.length) initial = 0;
      activate(initial, false);

      // Keep active state stable if DOM mutates (storefront only)
      if (!IS_EDITOR && window.MutationObserver) {
        try {
          const mo = new MutationObserver(() => {
            const cur = allButtons.findIndex(b => b.getAttribute("aria-selected") === "true");
            activate(cur > -1 ? cur : 0, false);
          });
          mo.observe(tabs, { childList: true, subtree: true });
          tabs.__etTabsObserver = mo;
        } catch(_) {}
      }
    });
  }

/* ========================================================================
 * 40) Module 02 — Variant/price sync
 *  - Updates price/discount UI on variant change
 *  - Calls fulfilment updater for availability/countdown sync
 * ====================================================================== */

  function refreshPriceUI(section, variant) {
    if (!section?.isConnected || !variant) return;
    const root  = $(section, ".pro-price"); if (!root) return;
    const rrpEl = $(root, ".pro-price__rrp");
    const curEl = $(root, ".pro-price__current");
    const disc  = $(root, ".pro-price__discount");
    const badge = $(section, ".discount-badge-hl");

    const sale = Number(variant.price);
    const rrp  = Number(variant.compare_at_price||0);
    const onSale = rrp>sale && rrp>0;

    if (curEl) curEl.textContent = formatMoney(sale);

    if (rrpEl) {
      if (onSale) {
        rrpEl.style.display = "";
        rrpEl.innerHTML = `RRP <s>${formatMoney(rrp)}</s>`;
      } else rrpEl.style.display = "none";
    }

    if (disc) {
      if (onSale) {
        const pct = Math.round(((rrp-sale)*100)/rrp);
        disc.style.display = "";
        disc.textContent = `${pct}% OFF RRP`;
      } else disc.style.display = "none";
    }

    if (badge) {
      badge.style.display = onSale ? "" : "none";
      if (onSale) {
        const pct = Math.round(((rrp-sale)*100)/rrp);
        const t = $(badge, "span"); if (t) t.textContent = `${pct}% OFF RRP`;
      }
    }
  }

  function hydrateInitialVariant(section) {
    let product = null;
    try { product = JSON.parse(section.getAttribute("data-product-json") || "{}"); } catch(_){}
    const currentId = section.querySelector('[data-variant-id-input]')?.value;
    return (
      product?.variants?.find(v => String(v.id) === String(currentId)) ||
      product?.variants?.[0] || null
    );
  }

  function initVariantSync(scope = document) {
    $$(scope, ".pro-product").forEach(section => {
      if (isReady(section, "etVariantReady")) return;
      markReady(section, "etVariantReady");

      // (A) Theme/OS2.0 event integration
      on(section, "variant:change", (evt) => {
        const variant = evt.detail?.variant || window?.current_variant;
        refreshPriceUI(section, variant);
      });

      // (B) Native selects (fallback) — compute a variant by selected options
      const selects = $$(section, 'select[name^="options"]');
      if (selects.length) {
        on(section, "change", (e) => {
          if (!e.target.matches || !e.target.matches('select[name^="options"]')) return;
          let product = null;
          try { product = JSON.parse(section.getAttribute("data-product-json") || "{}"); } catch(_){}
          const picks = selects.map(s => s.value);
          let variant = product?.variants?.find(v => v.options?.every((opt,i)=> String(opt)===String(picks[i])));
          if (!variant && product?.variants?.length) variant = product.variants[0];
          refreshPriceUI(section, variant);
          });
      }

      // (C) Initial paint
      const initial = hydrateInitialVariant(section);
      if (initial) {
        refreshPriceUI(section, initial);
      }
    });
  }

/* ========================================================================
* 50) Module 03 — retired 2026-09-29
*  The ET-derived fulfilment row never initialised under Empire's markup and
*  its cart listener threw on document.dataset. The panel is now
*  snippets/bn-pdp-fulfilment.liquid + assets/bn-pdp-fulfilment.js, using
*  the rules in assets/pdp-fulfilment-core.js shared with the ET theme.
* ====================================================================== */

/* ========================================================================
 * 60) Module 04 — Thumbnails fallback (simple “swap big media”)
 *  - Skips if a gallery library has already taken over
 * ====================================================================== */

  const galleryInitialized = (root) =>
    root.querySelector(
      ".flickity-enabled, .swiper, .swiper-initialized, .slick-initialized, .glider," +
      " [data-flickity], [data-swiper], .splide, .splide__track"
    );

  const keyOf = (el) =>
    el?.dataset?.mediaId ||
    el?.getAttribute?.("data-media-id") ||
    el?.id ||
    el?.getAttribute?.("aria-controls") ||
    (el?.getAttribute?.("href") || "").replace(/^#/, "") ||
    "";

  /*function initThumbsFallback(scope = document) {
    $$(scope, ".pro-product").forEach((section) => {
      if (isReady(section, "etThumbsReady")) return;
      markReady(section, "etThumbsReady");

      const rail = $(section, ".pro-product__thumbnails");
      const main = $(section, ".pro-product__media-main");
      if (!rail || !main) return;
      if (galleryInitialized(section)) return; // leave to the gallery

      const thumbs = $$(rail, ".pro-product__thumbnail-item");
      const media  = $$(main, ".pro-product__media-item");
      if (!thumbs.length || !media.length) return;

      const mediaKey = (m) => keyOf(m).replace(/^panel-|media-/, "");
      const thumbKey = (t) => keyOf(t).replace(/^panel-|media-/, "");
      const mediaIndexByKey = new Map();
      media.forEach((m, i) => {
        const k = mediaKey(m);
        if (k) mediaIndexByKey.set(k, i);
      });

      const visibleMediaIndex = () => {
        let idx = media.findIndex((m) => !m.hidden && getComputedStyle(m).display !== "none");
        if (idx > -1) return idx;
        idx = thumbs.findIndex((t) => t.classList.contains("is-active"));
        return idx > -1 ? idx : 0;
      };

      const select = (idx) => {
        if (idx < 0 || idx >= media.length) return;
        media.forEach((m) => { m.hidden = true; m.classList.remove("is-active"); });
        thumbs.forEach((t) => {
          t.classList.remove("is-active"); t.setAttribute("aria-current", "false"); t.tabIndex = -1;
        });
        media[idx].hidden = false;
        media[idx].classList.add("is-active");
        if (thumbs[idx]) {
          thumbs[idx].classList.add("is-active");
          thumbs[idx].setAttribute("aria-current", "true");
          thumbs[idx].tabIndex = 0;
        }
      };

      select(visibleMediaIndex());

      on(rail, "click", (e) => {
        const item = e.target.closest(".pro-product__thumbnail-item");
        if (!item) return;
        e.preventDefault();
        const k   = thumbKey(item);
        let index = (k && mediaIndexByKey.has(k)) ? mediaIndexByKey.get(k) : thumbs.indexOf(item);
        if (index < 0) index = 0;
        select(index);
      });

      on(rail, "keydown", (e) => {
        if (!["ArrowUp","ArrowLeft","ArrowDown","ArrowRight","Home","End","Enter"," "].includes(e.key)) return;
        const list = thumbs;
        let idx = list.indexOf(e.target.closest(".pro-product__thumbnail-item"));
        if (idx < 0) idx = visibleMediaIndex();

        let next = idx;
        switch (e.key) {
          case "ArrowRight":
          case "ArrowDown": next = (idx + 1) % list.length; break;
          case "ArrowLeft":
          case "ArrowUp":   next = (idx - 1 + list.length) % list.length; break;
          case "Home":      next = 0; break;
          case "End":       next = list.length - 1; break;
          case "Enter":
          case " ":
            e.preventDefault(); select(idx); return;
          default: return;
        }
        e.preventDefault();
        list[next]?.focus?.();
        select(next);
      });

      if (!IS_EDITOR && window.MutationObserver) {
        try {
          const mo = new MutationObserver(() => select(visibleMediaIndex()));
          mo.observe(main, { childList: true, subtree: true });
          section.__etThumbsObserver = mo;
        } catch (_) {}
      }
    });
  }*/

    function initThumbsFallback(scope = document) {
  $$(scope, ".pro-product").forEach((section) => {
    if (isReady(section, "etThumbsReady")) return;
    markReady(section, "etThumbsReady");

    // Bail if a gallery is actually running
    if (galleryInitialized(section)) return;

    const isVisible = (el) =>
      !!el &&
      getComputedStyle(el).display !== "none" &&
      getComputedStyle(el).visibility !== "hidden" &&
      el.offsetParent !== null;

    const pickVisible = (selector) => {
      const list = $$(section, selector);
      return list.find(isVisible) || list[0] || null;
    };

    // dynamic references (re-evaluated on breakpoint changes)
    let rail, main, thumbs, media, mediaIndexByKey;

    const keyOf = (el) =>
      el?.dataset?.mediaId ||
      el?.getAttribute?.("data-media-id") ||
      el?.id ||
      el?.getAttribute?.("aria-controls") ||
      (el?.getAttribute?.("href") || "").replace(/^#/, "") ||
      "";

    const mediaKey = (m) => keyOf(m).replace(/^panel-|media-/, "");
    const thumbKey = (t) => keyOf(t).replace(/^panel-|media-/, "");

    const refreshRefs = () => {
      rail   = pickVisible(".pro-product__thumbnails");
      main   = pickVisible(".pro-product__media-main");
      thumbs = rail ? $$(rail, ".pro-product__thumbnail-item") : [];
      media  = main ? $$(main, ".pro-product__media-item") : [];

      mediaIndexByKey = new Map();
      media.forEach((m, i) => {
        const k = mediaKey(m);
        if (k) mediaIndexByKey.set(k, i);
      });
    };

    refreshRefs();
    if (!rail || !main || !thumbs.length || !media.length) return;

    const visibleMediaIndex = () => {
      let idx = media.findIndex((m) => !m.hidden && getComputedStyle(m).display !== "none");
      if (idx > -1) return idx;
      idx = thumbs.findIndex((t) => t.classList.contains("is-active"));
      return idx > -1 ? idx : 0;
    };

    const select = (idx) => {
      if (!media || idx < 0 || idx >= media.length) return;
      media.forEach((m) => { m.hidden = true; m.classList.remove("is-active"); });
      thumbs.forEach((t) => {
        t.classList.remove("is-active");
        t.setAttribute("aria-current", "false");
        t.tabIndex = -1;
      });
      media[idx].hidden = false;
      media[idx].classList.add("is-active");
      if (thumbs[idx]) {
        thumbs[idx].classList.add("is-active");
        thumbs[idx].setAttribute("aria-current", "true");
        thumbs[idx].tabIndex = 0;
      }
    };

    select(visibleMediaIndex());

    // ---- Events (delegate on the section so it works for either rail) ----
    const activateThumb = (target) => {
      if (!target) return;
      // Re-evaluate which rail/main are visible (handles breakpoint switches)
      refreshRefs();
      if (!rail || !main) return;

      const k = thumbKey(target);
      let index = (k && mediaIndexByKey.has(k)) ? mediaIndexByKey.get(k) : thumbs.indexOf(target);
      if (index < 0) index = 0;
      select(index);
    };

    // CLICK
    section.addEventListener("click", (e) => {
      const item = e.target.closest(".pro-product__thumbnail-item");
      if (!item || !section.contains(item)) return;
      // no preventDefault on click (avoids passive/touch issues)
      activateThumb(item);
    }, false);

    // TOUCH (treat short taps as clicks; ignore swipes)
    let touchStartX = 0, touchStartY = 0;
    section.addEventListener("touchstart", (e) => {
      const t = e.touches && e.touches[0];
      if (t) { touchStartX = t.clientX; touchStartY = t.clientY; }
    }, { passive: true });

    section.addEventListener("touchend", (e) => {
      const item = e.target.closest(".pro-product__thumbnail-item");
      if (!item || !section.contains(item)) return;
      const t = e.changedTouches && e.changedTouches[0];
      if (!t) return;
      const dx = Math.abs(t.clientX - touchStartX);
      const dy = Math.abs(t.clientY - touchStartY);
      if (dx < 10 && dy < 10) { // tap, not swipe
        activateThumb(item);
      }
    }, { passive: true });

    // KEYBOARD (unchanged)
    on(section, "keydown", (e) => {
      if (!["ArrowUp","ArrowLeft","ArrowDown","ArrowRight","Home","End","Enter"," "].includes(e.key)) return;
      refreshRefs();
      const list = thumbs || [];
      let idx = list.indexOf(e.target.closest(".pro-product__thumbnail-item"));
      if (idx < 0) idx = visibleMediaIndex();

      let next = idx;
      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown": next = (idx + 1) % list.length; break;
        case "ArrowLeft":
        case "ArrowUp":   next = (idx - 1 + list.length) % list.length; break;
        case "Home":      next = 0; break;
        case "End":       next = list.length - 1; break;
        case "Enter":
        case " ":
          e.preventDefault(); select(idx); return;
        default: return;
      }
      e.preventDefault();
      list[next]?.focus?.();
      select(next);
    });

    // Keep in sync if DOM changes (e.g., active media swapped elsewhere)
    if (!IS_EDITOR && window.MutationObserver) {
      try {
        const mo = new MutationObserver(() => {
          refreshRefs();
          select(visibleMediaIndex());
        });
        mo.observe(section, { childList: true, subtree: true });
        section.__etThumbsObserver = mo;
      } catch (_) {}
    }

    // Re-evaluate on breakpoint/orientation changes
    const mq = window.matchMedia("(max-width: 990px)");
    mq.addEventListener?.("change", refreshRefs);
    window.addEventListener("resize", refreshRefs, { passive: true });
    window.addEventListener("orientationchange", refreshRefs, { passive: true });
  });
}


/* ========================================================================
 * 70) Module 05 — Zoom overlay (PSWP adapter + fallback)
 *  - Uses theme trigger if present; otherwise shows our overlay
 *  - Global delegate bound once
 * ====================================================================== */

  const TRIGGER_SEL =
    '[data-zoom-button], .pro-product__zoom-btn, .t4s-pr__pswp-btn, [data-t4s-pswp-btn], .pswp__open, [data-open-zoom], .product__zoom-btn';

function getActiveZoomData(section) {
  if (!section) return null;

  const main = section.querySelector('.pro-product__media-main');

  const isVisible = (el) => {
    if (!el) return false;
    if (el.hidden) return false;
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    // offsetParent null often indicates not visible (except for position:fixed)
    if (el.offsetParent === null && cs.position !== 'fixed') return false;
    return true;
  };

  // 1) collect .is-active nodes first (common case)
  const activeNodes = Array.from(section.querySelectorAll('.pro-product__media-item.is-active'));

  let active = null;

  // 2) prefer the active inside .pro-product__media-main that is visible
  if (activeNodes.length && main) {
    const insideMainVisible = activeNodes.filter(a => main.contains(a) && isVisible(a));
    if (insideMainVisible.length) {
      active = insideMainVisible[insideMainVisible.length - 1]; // pick last inside main
    }
  }

  // 3) otherwise prefer any visible .is-active (pick last visible)
  if (!active && activeNodes.length) {
    const visibleActives = activeNodes.filter(isVisible);
    if (visibleActives.length) active = visibleActives[visibleActives.length - 1];
  }

  // 4) otherwise if there are active nodes, pick the last one
  if (!active && activeNodes.length) active = activeNodes[activeNodes.length - 1];

  // 5) fallbacks — try to find a visible item in the main container
  if (!active && main) {
    active = Array.from(main.querySelectorAll('.pro-product__media-item')).find(isVisible) || main.querySelector('.pro-product__media-item');
  }

  // 6) final fallbacks across the whole section
  if (!active) {
    active =
      Array.from(section.querySelectorAll('.pro-product__media-item[aria-hidden="false"]')).find(isVisible) ||
      section.querySelector('.pro-product__media-item:not([hidden])') ||
      section.querySelector('.pro-product__media-item');
  }

  if (!active) return null;

  const mediaEl = active.querySelector('img, video');
  if (!mediaEl) return null;

  const src =
    active.getAttribute('data-zoom-src') ||
    mediaEl.getAttribute('data-zoom-src') ||
    mediaEl.currentSrc ||
    mediaEl.src ||
    '';

  const alt = mediaEl.getAttribute('alt') || '';

  return src ? { src, alt, img: mediaEl } : null;
}


  function ensureOverlay(){
    let modal = document.getElementById("pro-zoom-modal");
    if (modal) return modal;

    modal = document.createElement("div");
    modal.id = "pro-zoom-modal";
    modal.className = "pro-zoom";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-hidden", "true");
    modal.innerHTML = `
      <div class="pro-zoom__backdrop" data-close></div>
      <div class="pro-zoom__dialog" aria-live="polite">
        <div class="pro-zoom__stage"><img class="pro-zoom__image" alt="" /></div>
        <button class="pro-zoom__close" type="button" aria-label="Close" data-close>×</button>
      </div>`;
    document.body.appendChild(modal);
    return modal;
  }

  // Very small overlay impl (pinch/wheel/drag). For brevity, only essentials.
  (function attachOverlay(){
    if (ONCE.zoomOverlay) return; ONCE.zoomOverlay = true;

    const root  = ensureOverlay();
    const stage = root.querySelector('.pro-zoom__stage');
    const img   = root.querySelector('.pro-zoom__image');

    let s = 1, x = 0, y = 0, lastActive = null;
    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
    const render = () => {
      stage.style.setProperty('--zs', s);
      stage.style.setProperty('--zx', `${x}px`);
      stage.style.setProperty('--zy', `${y}px`);
    };
    const reset  = () => { s = 1; x = 0; y = 0; render(); };

    function show(src, alt='') {
      const modal = ensureOverlay();
      const imgEl = modal.querySelector('.pro-zoom__image');
      lastActive = document.activeElement;

      imgEl.src = src; imgEl.alt = alt || '';
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('pro-zoom--open', 'is-zoom-open');

      reset();
      imgEl.addEventListener('wheel', (e)=>{ e.preventDefault(); s = clamp(s * (e.deltaY<0?1.1:0.9), 1, 4); render(); }, { passive:false });

      let dragging=false, sx=0, sy=0, ox=0, oy=0;
      imgEl.addEventListener('pointerdown', e=>{ dragging=true; sx=e.clientX; sy=e.clientY; ox=x; oy=y; });
      imgEl.addEventListener('pointermove',  e=>{ if(!dragging) return; x = ox + (e.clientX-sx); y = oy + (e.clientY-sy); render(); });
      imgEl.addEventListener('pointerup',    ()=> dragging=false);
      imgEl.addEventListener('pointercancel',()=> dragging=false);

      modal.addEventListener('click', (ev) => {
        if (ev.target.matches('[data-close], .pro-zoom__backdrop')) hide();
      });

      window.addEventListener('keydown', onKey);
      (modal.querySelector('.pro-zoom__close') || modal).focus({ preventScroll:true });
    }

    function hide() {
      const modal = document.getElementById('pro-zoom-modal');
      if (!modal) return;
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('pro-zoom--open', 'is-zoom-open');
      window.removeEventListener('keydown', onKey);
      const closeBtn = modal.querySelector('.pro-zoom__close');
      if (closeBtn) closeBtn.blur();
      if (lastActive?.focus) setTimeout(() => lastActive.focus({ preventScroll:true }), 0);
      lastActive = null;
    }

    function onKey(e){ if (e.key === 'Escape') hide(); if (e.key === '0') { s=1; x=0; y=0; render(); } }

    // Publish overlay API
    window.ET_PRO = Object.assign(window.ET_PRO || {}, { showZoom: show, hideZoom: hide, getActiveZoomData });
  })();

// Open zoom using the nearest section only (no blind fallback)
function openZoomFor(section, trigger) {
  if (!section) return;
  // Prefer theme-native PSWP trigger inside the same section
  const native = section.querySelector('.t4s-pr__pswp-btn,[data-t4s-pswp-btn],.pswp__open,[data-open-zoom],.product__zoom-btn');
  if (native && native !== trigger) {
    native.click();
    return;
  }

  const z = getActiveZoomData(section);
  if (z?.src) window.ET_PRO.showZoom(z.src, z.alt);
}


// Init zoom (global delegate). Ensure we use closest .pro-product only.
function initZoom(scope = document) {
  const sections = (scope && scope.matches && scope.matches('.pro-product')) ? [scope] : $$(scope, '.pro-product');
  sections.forEach((s) => { if (!isReady(s, "etZoomReady")) markReady(s, "etZoomReady"); });

  if (!ONCE.zoomDelegate) {
    ONCE.zoomDelegate = true;

    // Delegate on document — but require trigger.closest('.pro-product')
    on(document, "click", (ev) => {
      const trigger = ev.target.closest(TRIGGER_SEL);
      if (!trigger) return;

      // Must use the closest section only — don't fallback to the first product on the page
      const section = trigger.closest(".pro-product");
      if (!section) return;

      // preventDefault is fine here for link/button triggers
      ev.preventDefault();
      openZoomFor(section, trigger);
    });
  }
}

/* ==========================================================================
 * 60) Module 06 — Thumb nav (Up/Down + next/prev selection, with internal index)
 *  - Keeps its own pointer so it doesn’t rely on theme classes updating
 * ========================================================================== */
(() => {
  const NS  = (window.ET_PRO = window.ET_PRO || {});
  const MAP = new WeakMap();

  const canScroll  = (el) => el && el.scrollHeight > el.clientHeight + 1;
  const atTop      = (el) => el.scrollTop <= 0;
  const atBottom   = (el) => Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;

  function bindThumbCol(col){
    if (!col || MAP.has(col)) return;

    const list   = col.querySelector('[data-thumbnails-container], .pro-product__thumbnails');
    const upBtn  = col.querySelector('[data-thumbnail-scroll="up"], .pro-product__thumbnav--up');
    const dnBtn  = col.querySelector('[data-thumbnail-scroll="down"], .pro-product__thumbnav--down');
    if (!list || (!upBtn && !dnBtn)) return;

    let thumbs = Array.from(list.querySelectorAll('.pro-product__thumbnail-item, [role="option"]'));
    // initialise pointer from DOM, else 0
    let cur = Math.max(0, thumbs.findIndex(t =>
      t.classList.contains('is-active') || t.getAttribute('aria-selected') === 'true'
    ));
    if (cur === -1) cur = 0;

    const clamp = (i) => Math.max(0, Math.min(thumbs.length - 1, i));

    const pick = (i) => {
      if (!thumbs.length) return;
      cur = clamp(i);
      // fire the native behaviour
      thumbs[cur].click();
      try { thumbs[cur].scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch(_) {}
      // reflect aria if theme doesn’t
      thumbs.forEach((t,j) => {
        t.classList.toggle('is-active', j === cur);
        t.setAttribute('aria-selected', j === cur ? 'true' : 'false');
        t.tabIndex = (j === cur ? 0 : -1);
      });
    };

    // wire buttons: move pointer then pick
    if (upBtn) upBtn.addEventListener('click',  () => pick(cur - 1));
    if (dnBtn) dnBtn.addEventListener('click',  () => pick(cur + 1));

    // clicking a thumb updates our pointer
    thumbs.forEach((t,i) => t.addEventListener('click', () => { cur = i; }, { passive: true }));

    // keep button enabled/disabled states sensible
    const setBtnStates = () => {
      const scrollable = canScroll(list);
      const single = thumbs.length <= 1;
      const off = !scrollable && single;
      [upBtn, dnBtn].forEach(btn => {
        if (!btn) return;
        btn.disabled = off;
        btn.classList.toggle('is-disabled', !!off);
        btn.hidden = false;
      });
      if (upBtn) { const dis = off || cur <= 0; upBtn.disabled = dis; upBtn.classList.toggle('is-disabled', dis); }
      if (dnBtn) { const dis = off || cur >= thumbs.length - 1; dnBtn.disabled = dis; dnBtn.classList.toggle('is-disabled', dis); }
    };

    // observe changes to the list (editor/gallery churn)
    const mo = new MutationObserver(() => {
      thumbs = Array.from(list.querySelectorAll('.pro-product__thumbnail-item, [role="option"]'));
      cur = clamp(cur);
      setBtnStates();
    });
    mo.observe(list, { childList: true, subtree: true, attributes: true });

    list.addEventListener('scroll', setBtnStates, { passive: true });
    window.addEventListener('resize', setBtnStates);

    // initial paint
    requestAnimationFrame(() => { setBtnStates(); });

    MAP.set(col, () => {
      mo.disconnect();
      list.removeEventListener('scroll', setBtnStates);
      window.removeEventListener('resize', setBtnStates);
    });
  }

  NS.initThumbNav = function initThumbNav(root = document){
    const scope = (root.matches && root.matches('.pro-product')) ? root :
                  (root.querySelector && root.querySelector('.pro-product')) || document;
    scope.querySelectorAll('.pro-product__thumbcol, [data-thumbcol]').forEach(bindThumbCol);
  };

  document.addEventListener('DOMContentLoaded', () => NS.initThumbNav());
  document.addEventListener('shopify:section:load',   (e) => NS.initThumbNav(e.target));
  document.addEventListener('shopify:section:select', (e) => NS.initThumbNav(e.target));
})();


/* ========================================================================
 * 85) Module 07 — Quantity stepper (min/max/step aware + section-safe)
 *  - Hooks into markup with [data-qty], [data-qty-input], [data-qty-btn]
 *  - Disables +/- at limits, supports ArrowUp/ArrowDown keys
 *  - Syncs with variant:change for inventory-based max
 * ====================================================================== */
(function(){
  const NS = (window.ET_PRO = window.ET_PRO || {});
  const BOUND = new WeakSet();

  function clamp(val, min, max, step) {
    const v = isNaN(val) ? min : Number(val);
    const s = step > 0 ? step : 1;
    let n = Math.round(v / s) * s;
    if (!isFinite(min)) min = 1;
    if (!isFinite(max)) max = Infinity;
    if (n < min) n = min;
    if (n > max) n = max;
    return n;
  }

  function initQty(root = document) {
    const blocks = Array.from(root.querySelectorAll('[data-qty]'));
    blocks.forEach(block => {
      if (BOUND.has(block)) return;
      const input = block.querySelector('[data-qty-input], input[name="quantity"]');
      const minus = block.querySelector('[data-qty-btn="minus"], [name="minus"], [data-qty-minus]');
      const plus  = block.querySelector('[data-qty-btn="plus"],  [name="plus"],  [data-qty-plus]');
      if (!input) return;
      BOUND.add(block);

      const readMin  = () => Number(input.min || 1) || 1;
      const readMax  = () => {
        const m = input.getAttribute('max');
        if (m === null || m === '' || m === undefined) return Infinity;
        const n = Number(m);
        return isNaN(n) ? Infinity : n;
      };
      const readStep = () => {
        const n = Number(input.step || 1);
        return isNaN(n) || n <= 0 ? 1 : n;
      };

      const updateButtons = () => {
        const v = Number(input.value) || readMin();
        if (minus) minus.toggleAttribute('disabled', v <= readMin());
        if (plus)  plus.toggleAttribute('disabled',  v >= readMax());
      };

      const setValue = (next) => {
        const val = clamp(next, readMin(), readMax(), readStep());
        input.value = String(val);
        updateButtons();
        // Bubble change so themes/widgets can react
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      minus && minus.addEventListener('click', () => setValue((Number(input.value) || readMin()) - readStep()));
      plus  && plus.addEventListener('click',  () => setValue((Number(input.value) || readMin()) + readStep()));

      input.addEventListener('change', () => setValue(Number(input.value)));
      input.addEventListener('input',  updateButtons);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowUp')   { e.preventDefault(); plus && plus.click(); }
        if (e.key === 'ArrowDown') { e.preventDefault(); minus && minus.click(); }
      });

      // initial state
      if (!input.value) input.value = String(readMin());
      setValue(Number(input.value));
    });
  }

  // expose
  NS.initQty = initQty;

  // auto-init (boot will also call)
  document.addEventListener('DOMContentLoaded', () => initQty());
  document.addEventListener('shopify:section:load',   (e) => initQty(e.target || document));
  document.addEventListener('shopify:section:select', (e) => initQty(e.target || document));

  // Keep max/disabled in sync on variant change if theme dispatches it
  document.addEventListener('variant:change', (e) => {
    const v = e.detail && e.detail.variant;
    const scope = (e.target && e.target.closest && e.target.closest('[data-section-id]')) || document;
    if (!v) return;
    scope.querySelectorAll('[data-qty] [data-qty-input], [data-qty] input[name="quantity"]').forEach((input) => {
      const backorder = v.inventory_policy === 'continue';
      input.toggleAttribute('disabled', !v.available);
      if (backorder) input.removeAttribute('max');
      else if (typeof v.inventory_quantity === 'number') input.setAttribute('max', String(v.inventory_quantity));
      // clamp current value to new bounds
      const min = Number(input.min || 1) || 1;
      const max = input.hasAttribute('max') ? Number(input.max) || Infinity : Infinity;
      const step = Number(input.step || 1) || 1;
      const clamped = clamp(Number(input.value) || min, min, max, step);
      input.value = String(clamped);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
})();

/* ======================================
 * 90) BOOTSTRAP & EDITOR LIFECYCLE HOOKS
 * - Single source of initialization truth
 * - Always call modules via window.ET_PRO
 * ==================================== */

  // Namespace (create once; we fill in below)
  window.ET_PRO = window.ET_PRO || {};
  const NS = window.ET_PRO;

  const can = (fn) => typeof fn === "function";

  function boot(root = document) {
    try {
      if (FEATURES.tabs && can(NS.initTabs)) NS.initTabs(root);
      if (FEATURES.variantPrice) {
        if (can(NS.initVariantSync)) NS.initVariantSync(root);
        else if (can(NS.initVariants)) NS.initVariants(root); // alias fallback
      }
      if (FEATURES.thumbsFallback && can(NS.initThumbsFallback)) NS.initThumbsFallback(root);
      if (FEATURES.zoom && can(NS.initZoom)) NS.initZoom(root);
      if (FEATURES.thumbNav && can(NS.initThumbNav)) NS.initThumbNav(root);
      if (FEATURES.qty && can(NS.initQty)) NS.initQty(root);

    } catch (e) {
      console.error("ET_PRO boot failed:", e);
    }
  }

  // 1) When this script tag finishes (covers defer ordering)
  try {
    const s = document.getElementById("pdp-safe-js");
    if (s) s.addEventListener("load", () => boot(), { once: true });
  } catch (_) {}

  // 2) DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(), { once: true });
  } else {
    Promise.resolve().then(() => boot());
  }

  // 3) OS 2.0 section events
  document.addEventListener("shopify:section:load",   (e) => boot(e.target || document));
  document.addEventListener("shopify:section:select", (e) => boot(e.target || document));

  // Expose boot for manual testing: window.ET_PRO.boot()
  NS.boot = boot;

/* =========================
 * 99) PUBLIC API + ALIASES
 * (place INSIDE the main IIFE, right before it closes)
 * ======================= */
(function attachPublicAPI(window){
  var NS = (window.ET_PRO = window.ET_PRO || {});

  // Attach only if the function exists in this scope
  if (typeof initTabs === 'function')                 NS.initTabs = initTabs;
  if (typeof initVariantSync === 'function')         { NS.initVariantSync = initVariantSync; if (!NS.initVariants)  NS.initVariants  = initVariantSync; }
  if (typeof initThumbsFallback === 'function')      NS.initThumbsFallback = initThumbsFallback;
  if (typeof initZoom === 'function')                NS.initZoom = initZoom;
  if (typeof NS.initThumbNav === 'function')         NS.initThumbNav = NS.initThumbNav; // already attached in IIFE
  if (typeof NS.initQty === 'function')              NS.initQty = NS.initQty;
  if (typeof boot === 'function')                    NS.boot = boot;

  if (typeof VERSION   !== 'undefined') NS.version  = VERSION;
  if (typeof IS_EDITOR !== 'undefined') NS.isEditor = IS_EDITOR;
  if (typeof FEATURES  !== 'undefined') NS.features = FEATURES;
})(window);

})();
/* =======================================================================
 * Eco Traders — Purchase Options (One-time + AutoDelivery)
 * -----------------------------------------------------------------------
 * SAFE DROP-IN
 * - Builds custom Purchase Options UI
 * - Collapsible subscription body (reduced-motion aware)
 * - Remembers last plan
 * - Wires Shopify `selling_plan`
 * - Hides 3rd-party duplicate bits (Seal/Native)
 * - Segmented "Create New" / "Select Existing"
 *
 * NEW IN THIS BUILD:
 * - Feature icon swapper is invoked after first render
 * - If the feature area is mutated, icons are re-applied automatically
 * =======================================================================*/
(function () {
  /* ---------------------------------------------------------------------
   * 0) SAFE ROOTS + STATE KEYS
   * ------------------------------------------------------------------- */

  

  var ROOT = document.querySelector('#pro-product');
  if (!ROOT) return;

  if (ROOT.querySelector('.pro-product__variant-selector')) return;

  // Avoid duplicate mounts (e.g., section re-render)
  if (ROOT.querySelector('.et-purchase')) return;

  var FORM        = ROOT.querySelector('.pro-product__form');
  var VAR_INPUT   = ROOT.querySelector('[data-variant-id-input]');
  var PJSON_ATTR  = 'data-product-json';
  var LS_MODE_KEY = 'et:purchase_mode';
  var LS_PLAN_KEY = 'et:purchase_plan';
  if (!FORM) return;

  // Reduced motion preference
  var PREFERS_NO_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
   * 1) PRODUCT JSON / MONEY HELPERS
   * ------------------------------------------------------------------- */
  function productJSON(){
    try { return JSON.parse(ROOT.getAttribute(PJSON_ATTR)); } catch(e){ return null; }
  }
  function variantById(p, id){
    if (!p || !p.variants) return null;
    for (var i=0;i<p.variants.length;i++) if (p.variants[i].id === id) return p.variants[i];
    return null;
  }
  function currentVariantId(){
    if (VAR_INPUT && VAR_INPUT.value) return Number(VAR_INPUT.value);
    var p = productJSON();
    if (!p || !p.variants || !p.variants.length) return null;
    for (var i=0;i<p.variants.length;i++) if (p.variants[i].available) return p.variants[i].id;
    return p.variants[0].id;
  }
  function plansForVariant(vid, p){
    if (!p) return { group:null, plans:[] };
    var v = variantById(p, vid);
    var allowed = {};
    var allocs = (v && v.selling_plan_allocations) || [];
    for (var i=0;i<allocs.length;i++) allowed[ allocs[i].selling_plan_id ] = true;
    var group = (p.selling_plan_groups && p.selling_plan_groups[0]) || null;
    var plans = (group && group.selling_plans) ? group.selling_plans.slice() : [];

    if (allocs.length) plans = plans.filter(function(sp){ return !!allowed[sp.id]; });
    return { group:group, plans:plans };
  }
  function planLabel(sp){
    if (sp && sp.options && sp.options.length){
      var text = sp.options.map(function(o){ return o.value; }).join(' ');
      return /^every/i.test(text) ? text : ('Every ' + text);
    }
    return (sp && sp.name) || 'Plan';
  }
  function priceWithPlanCents(variant, sp){
    var cents = variant.price;
    var adj = (sp && Array.isArray(sp.price_adjustments) && sp.price_adjustments[0]) || null;
    if (!adj) return cents;
    var type = adj.value_type || adj.adjustment_type;
    var val  = (typeof adj.value === 'number') ? adj.value : adj.adjustment_value;
    if (type === 'percentage')   return Math.round(cents * (100 - val) / 100);
    if (type === 'fixed_amount') return Math.max(0, cents - val);
    if (type === 'price')        return val;
    return cents;
  }
  function money(cents){
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD';
    try { return new Intl.NumberFormat('en-AU', { style:'currency', currency:cur }).format(cents/100); }
    catch(e){ return 'A$' + (cents/100).toFixed(2); }
  }

  /* ---------------------------------------------------------------------
   * 2) REQUIRED HIDDEN INPUT → Shopify selling_plan
   * ------------------------------------------------------------------- */
  var spInput = document.createElement('input');
  spInput.type  = 'hidden';
  spInput.name  = 'selling_plan';
  spInput.setAttribute('data-sub-selling-plan','1');
  FORM.appendChild(spInput);

  /* ---------------------------------------------------------------------
   * 3) BUILD UI (cards + subscription body)
   * ------------------------------------------------------------------- */
  var wrap = document.createElement('div');
  wrap.className = 'et-purchase';
  wrap.setAttribute('role','radiogroup');
  wrap.innerHTML = [
    // One-time
    '<button type="button" class="et-option et-option--one is-active" role="radio" aria-checked="true" data-mode="one">',
      '<span class="et-option__radio" aria-hidden="true"></span>',
      '<span class="et-option__label">One-time purchase</span>',
      '<span class="et-option__price" data-one-price></span>',
    '</button>',

    // Subscription
    '<div class="et-option et-option--sub" role="radio" aria-checked="false" data-mode="sub" tabindex="0">',
      '<div class="sub-card">',
        '<div class="sub-head">',
          '<div class="sub-head-left">',
            '<span class="sub-radio" aria-hidden="true"></span>',
            '<span class="sub-title">AutoDelivery</span>',
          '</div>',
          '<div class="sub-price" data-sub-price></div>',
        '</div>',

        '<div class="sub-body" aria-hidden="true">',


          /* NOTE: the three inline SVGs below are just placeholders.
             The icon swapper defined later will replace them with the
             Lucide/hero versions consistently after first render. */
/* NOTE: Lucide icons inline */
'<div class="sub-features" role="list" aria-label="AutoDelivery benefits">',

  // 1) Calendar Days (Lucide)
  '<div class="sub-feature" role="listitem">',
    '<span class="sub-ico" aria-hidden="true">',
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">',
        '<path d="M8 2v4"></path>',
        '<path d="M16 2v4"></path>',
        '<rect width="18" height="18" x="3" y="4" rx="2"></rect>',
        '<path d="M3 10h18"></path>',
        '<path d="M8 14h.01"></path>',
        '<path d="M12 14h.01"></path>',
        '<path d="M16 14h.01"></path>',
        '<path d="M8 18h.01"></path>',
        '<path d="M12 18h.01"></path>',
        '<path d="M16 18h.01"></path>',
      '</svg>',
    '</span>',
    '<span>Adjust to your schedule</span>',
  '</div>',

  // 2) Pause (Lucide)
  '<div class="sub-feature" role="listitem">',
    '<span class="sub-ico" aria-hidden="true">',
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">',
        '<rect x="6" y="5" width="4" height="14" rx="1.5"></rect>',
        '<rect x="14" y="5" width="4" height="14" rx="1.5"></rect>',
      '</svg>',
    '</span>',
    '<span>Easily pause, skip or cancel</span>',
  '</div>',

  // 3) Package Check (Lucide)
  '<div class="sub-feature" role="listitem">',
    '<span class="sub-ico" aria-hidden="true">',
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">',
        '<path d="m16 16 2 2 4-4"></path>',
        '<path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14"></path>',
        '<path d="m7.5 4.27 9 5.15"></path>',
        '<polyline points="3.29 7 12 12 20.71 7"></polyline>',
        '<line x1="12" y1="22" x2="12" y2="12"></line>',
      '</svg>',
    '</span>',
    '<span>Hassle free delivery</span>',
  '</div>',

'</div>',


          '<div class="sub-actions">',
            '<button type="button" class="sub-tab is-active" data-sub-create>Create New</button>',
            '<button type="button" class="sub-tab" data-sub-existing>Select Existing</button>',
          '</div>',

          // Deliver interval
          '<div class="sub-field" data-sub-deliver-field>',
            '<span class="sub-label">Deliver</span>',
            '<select class="sub-select" data-sub-interval></select>',
          '</div>',

          // Existing-contract select (hidden until “Select Existing”)
          '<div class="sub-field" data-sub-existing-field hidden>',
            '<span class="sub-label">Choose existing AutoDelivery</span>',
            '<select class="sub-select" data-sub-existing-select disabled>',
              '<option value="__none">No existing AutoDeliveries</option>',
            '</select>',
          '</div>',

          // “one cycle only” checkbox (hidden until “Select Existing”)
          '<label class="sub-once sub-once-row" data-sub-once-row hidden>',
            '<input type="checkbox" data-sub-once style="width:16px;height:16px;">',
            '<span>Add for only one cycle. Remove this item after I receive it.</span>',
          '</label>',

          '<div class="sub-foot"><a class="sub-learn" href="https://ecotraders.com.au/pages/auto-deliver-subscriptions">Learn more ›</a></div>',
        '</div>',
      '</div>',
    '</div>'
  ].join('');

  // Insert after badges/app blocks for stable position
  function anchorForInsert(){
    return ROOT.querySelector('.pro-buy-row--badges') ||
           ROOT.querySelector('.sls-purchase-options-container, .seal-purchase-options-container, .sls-option-container') ||
           FORM;
  }
  var anchor = anchorForInsert();
  anchor.parentNode.insertBefore(wrap, anchor.nextSibling);

  /* ---------------------------------------------------------------------
   * 4) SHORTCUTS
   * ------------------------------------------------------------------- */
  var oneBtn      = wrap.querySelector('[data-mode="one"]');
  var subBox      = wrap.querySelector('[data-mode="sub"]');
  var bodyEl      = wrap.querySelector('.sub-body');
  var onePriceEl  = wrap.querySelector('[data-one-price]');
  var subPriceEl  = wrap.querySelector('[data-sub-price]');
  var intervalSel = wrap.querySelector('[data-sub-interval]');
  var deliverField= wrap.querySelector('[data-sub-deliver-field]');
  var createBtn   = wrap.querySelector('[data-sub-create]');
  var existingBtn = wrap.querySelector('[data-sub-existing]');
  var actions     = wrap.querySelector('.sub-actions');

  // NEW: elements for “Select Existing” UI
  var existingField  = wrap.querySelector('[data-sub-existing-field]');
  var existingSelect = wrap.querySelector('[data-sub-existing-select]');
  var onceRow        = wrap.querySelector('[data-sub-once-row]');
  var onceInput      = wrap.querySelector('[data-sub-once]');

  // IDs for aria-controls (created if missing)
  if (actions && !actions.id) actions.id = 'et-sub-actions';
  if (bodyEl  && !bodyEl.id)  bodyEl.id  = 'et-sub-body';

  /* ---------------------------------------------------------------------
   * 5) LOGIN GATE
   * ------------------------------------------------------------------- */
  function isLoggedIn(){
    try { if (window.Shopify && Shopify.customer && Shopify.customer.id) return true; } catch(e){}
    try {
      var sa = window.ShopifyAnalytics && ShopifyAnalytics.meta;
      if (sa && (sa.page && sa.page.customerId)) return true;
      if (sa && sa.customerId) return true;
    } catch(e){}
    if (document.querySelector('a[href*="/account/logout"], form[action*="/account/logout"]')) return true;
    return false;
  }

  /* ---------------------------------------------------------------------
   * 6) EXPAND/COLLAPSE HELPERS
   * ------------------------------------------------------------------- */
  function expandBodyNow(force){
    if (!bodyEl || !subBox) return;
    var shouldOpen = !!force || subBox.getAttribute('aria-checked') === 'true';
    if (!shouldOpen) return;

    bodyEl.setAttribute('aria-hidden','false');
    subBox.classList.add('is-active');
    bodyEl.style.display = 'block';
    if (PREFERS_NO_MOTION){
      bodyEl.style.maxHeight = 'none';
    } else {
      bodyEl.style.maxHeight = '9999px';
      requestAnimationFrame(function(){
        bodyEl.style.maxHeight = bodyEl.scrollHeight + 'px';
      });
    }
  }
  function closeBodyNow(){
    if (!bodyEl || !subBox) return;
    bodyEl.setAttribute('aria-hidden','true');
    if (PREFERS_NO_MOTION){
      bodyEl.style.maxHeight = '0px';
    } else {
      bodyEl.style.maxHeight = bodyEl.scrollHeight + 'px';
      requestAnimationFrame(function(){ bodyEl.style.maxHeight = '0px'; });
    }
    subBox.classList.remove('is-active');
  }
  function adjustBodyHeight(){
    if (!bodyEl) return;
    if (bodyEl.getAttribute('aria-hidden') === 'false'){
      bodyEl.style.maxHeight = bodyEl.scrollHeight + 'px';
    }
  }

  /* ---------------------------------------------------------------------
   * 7) MODE (one-time vs subscription) + INTERVALS + PRICING
   * ------------------------------------------------------------------- */
  function setMode(mode){
    var isOne = (mode === 'one');

    // Visual mutual exclusivity
    if (isOne) {
      oneBtn.classList.add('is-active');
      subBox.classList.remove('is-active');
      
    } else {
      oneBtn.classList.remove('is-active');
      subBox.classList.add('is-active');
    }

    // ARIA radio state
    oneBtn.setAttribute('aria-checked', String(isOne));
    subBox.setAttribute('aria-checked', String(!isOne));

    // Hidden selling_plan input
    spInput.value = isOne ? '' : String(intervalSel.value || '');

    // Open/close
    if (isOne) { closeBodyNow(); } else { expandBodyNow(); }

    try { localStorage.setItem(LS_MODE_KEY, isOne ? 'one' : 'sub'); } catch(e){}
  }

  function mountIntervals(plans){
    intervalSel.innerHTML = '';
    for (var i=0;i<plans.length;i++){
      var sp = plans[i];
      var opt = document.createElement('option');
      var label = planLabel(sp);
      if (i===0) label += ' (recommended)';
      opt.value = String(sp.id);
      opt.textContent = label;
      intervalSel.appendChild(opt);
    }
    try {
      var saved = localStorage.getItem(LS_PLAN_KEY);
      if (saved && intervalSel.querySelector('option[value="'+saved+'"]')) intervalSel.value = saved;
    } catch(e){}
  }

  /* ---------------------------------------------------------------------
   * 8) HELPER MESSAGE (when “Existing” isn’t available)
   * ------------------------------------------------------------------- */
  function showHelper(){
    var msg = wrap.querySelector('.sub-empty');
    if (!msg){
      var a = actions || wrap.querySelector('.sub-actions');
      if (!a) return;
      msg = document.createElement('div');
      msg.className = 'sub-empty';
      msg.textContent = "You have no existing AutoDeliveries. Please select 'Create New' and add the item to your cart to create a new AutoDelivery";
      a.after(msg);
    }
    expandBodyNow(true);
  }
  function hideHelper(){
    var msg = wrap.querySelector('.sub-empty');
    if (msg) msg.remove();
    adjustBodyHeight();
  }
  function isExistingGated(){
    return (existingBtn && (
      existingBtn.getAttribute('aria-disabled') === 'true' ||
      !!existingBtn.dataset.gated
    ));
  }

  /* ---------------------------------------------------------------------
   * 9) “SELECT EXISTING” UI — POPULATE + TOGGLE
   * ------------------------------------------------------------------- */
  function populateExistingSelect(){
    if (!existingSelect) return;
    var gated = isExistingGated();
    var list  = Array.isArray(window.__etExistingSubs) ? window.__etExistingSubs : [];

    existingSelect.innerHTML = '';

    if (gated || !list.length){
      var opt = document.createElement('option');
      opt.value = '__none';
      opt.textContent = 'No existing AutoDeliveries';
      existingSelect.appendChild(opt);
      existingSelect.disabled = true;
      return;
    }

    // Has subscriptions
    existingSelect.disabled = false;
    for (var i=0;i<list.length;i++){
      var o = document.createElement('option');
      o.value = String(list[i].id);
      o.textContent = list[i].label || ('AutoDelivery #' + (i+1));
      existingSelect.appendChild(o);
    }
  }

  function toggleExistingUI(show){
    if (!existingField || !onceRow || !deliverField) return;
    if (show){
      deliverField.hidden  = true;
      existingField.hidden = false;
      onceRow.hidden       = false;
      populateExistingSelect();
    } else {
      deliverField.hidden  = false;
      existingField.hidden = true;
      onceRow.hidden       = true;
    }
    adjustBodyHeight();
  }

  /* ---------------------------------------------------------------------
   * 10) SEGMENTED TABS — ARIA + KEYBOARD
   * ------------------------------------------------------------------- */
  function initTabsA11y(){
    if (!actions || !createBtn || !existingBtn) return;
    // Roles
    actions.setAttribute('role','tablist');
    createBtn.setAttribute('role','tab');
    existingBtn.setAttribute('role','tab');
    // Controls
    if (bodyEl && bodyEl.id){
      createBtn.setAttribute('aria-controls', bodyEl.id);
      existingBtn.setAttribute('aria-controls', bodyEl.id);
    }
    // Default selection
    setActiveTab('create');
    toggleExistingUI(false);

    // Keyboard: Left/Right to switch
    actions.addEventListener('keydown', function(e){
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft'){
        e.preventDefault();
        var next = (document.activeElement === createBtn) ? existingBtn : createBtn;
        next.focus();
        next.click();
      }
    });
    // Radios (space/enter)
    subBox.addEventListener('keydown', function(e){
      if (e.key === ' ' || e.key === 'Enter'){ e.preventDefault(); setMode('sub'); }
    });
    oneBtn.addEventListener('keydown', function(e){
      if (e.key === ' ' || e.key === 'Enter'){ e.preventDefault(); setMode('one'); }
    });
  }
  function setActiveTab(which){
    var isCreate = (which === 'create');
    if (createBtn){
      createBtn.classList.toggle('is-active', isCreate);
      createBtn.setAttribute('aria-selected', String(isCreate));
      createBtn.tabIndex = isCreate ? 0 : -1;
    }
    if (existingBtn){
      existingBtn.classList.toggle('is-active', !isCreate);
      existingBtn.setAttribute('aria-selected', String(!isCreate));
      existingBtn.tabIndex = !isCreate ? 0 : -1;
    }
    // Toggle fields visibility
    toggleExistingUI(!isCreate);
  }

  /* ---------------------------------------------------------------------
   * 11) RENDER (initial + on variant change)
   * ------------------------------------------------------------------- */
  function render(){
    var p   = productJSON();
    var vid = currentVariantId();
    if (!p || !vid) return;

    var v = variantById(p, vid);
    if (onePriceEl) onePriceEl.textContent = money(v.price);

    var pack  = plansForVariant(vid, p);
    var plans = pack.plans || [];

    console.log(pack.group);

    if (!plans.length){
      subBox.style.display = 'none';
      setMode('one');
      return;
    } else {
      subBox.style.display = '';
    }

    mountIntervals(plans);

    var chosen = null;
    for (var i=0;i<plans.length;i++){
      if (String(plans[i].id) === String(intervalSel.value)) { chosen = plans[i]; break; }
    }
    if (!chosen) chosen = plans[0];
    if (subPriceEl) subPriceEl.textContent = money( priceWithPlanCents(v, chosen) );

    // Always default to One-time on load
    try { localStorage.removeItem(LS_MODE_KEY); } catch (e) {}
    setMode('one');
    setActiveTab('create');   // ensures deliver visible; existing UI hidden

    // Gate “Select Existing” by login (no real disabled)
    if (existingBtn){
      if (isLoggedIn()){
        existingBtn.removeAttribute('aria-disabled');
        delete existingBtn.dataset.gated;
        existingBtn.removeAttribute('title');
      } else {
        existingBtn.setAttribute('aria-disabled','true');
        existingBtn.dataset.gated = 'login';
        existingBtn.title = 'Sign in to add to an existing AutoDelivery';
        setActiveTab('create');
        hideHelper();
      }
    }

    setTimeout(adjustBodyHeight, 0);
  }

  /* ---------------------------------------------------------------------
   * 12) EVENTS — radios, intervals, tabs
   * ------------------------------------------------------------------- */
  oneBtn.addEventListener('click', function(){ setMode('one'); });
  subBox.addEventListener('click', function(e){
    if (e.target.closest('select,button,a,input,label')) return; // ignore inner controls
    setMode('sub');
  });

  // Interval change → sub price + save plan
  intervalSel.addEventListener('change', function(){
    var p   = productJSON();
    var vid = currentVariantId();
    var v   = variantById(p, vid);
    var plans = plansForVariant(vid, p).plans || [];
    var chosen = null;
    for (var i=0;i<plans.length;i++){
      if (String(plans[i].id) === String(intervalSel.value)) { chosen = plans[i]; break; }
    }
    if (chosen && v && subPriceEl) {
      subPriceEl.textContent = money( priceWithPlanCents(v, chosen) );
      try { localStorage.setItem(LS_PLAN_KEY, String(intervalSel.value)); } catch(e){}
    }
    spInput.value = String(intervalSel.value || '');
    adjustBodyHeight();
  });

  // Tabs: Create New / Select Existing
  initTabsA11y();

  if (createBtn){
    createBtn.addEventListener('click', function(ev){
      ev.stopPropagation();
      setActiveTab('create');
      hideHelper();
      adjustBodyHeight();
    });
  }

  if (existingBtn){
    existingBtn.addEventListener('click', function(ev){
      ev.stopPropagation();
      setActiveTab('existing');  // show existing-select + once checkbox

      var hasExisting = !!window.__etHasExistingSubs;
      if (isExistingGated() || !hasExisting){
        showHelper();            // surface guidance if not available
      } else {
        hideHelper();
      }
    });

    // Convenience: double-click → account (when not gated)
    existingBtn.addEventListener('dblclick', function(){
      if (!isExistingGated()) window.location.href = '/account';
    });
  }

  // Variant change → re-render
  if (VAR_INPUT){
    ['change','input'].forEach(function(evt){ VAR_INPUT.addEventListener(evt, render); });
  }
  document.addEventListener('shopify:section:load', render);

  // Initial paint
  render();

  /* NEW: apply icon swap immediately after first render */
  if (window.ET_PRO && typeof window.ET_PRO.initFeatureIcons === 'function') {
    window.ET_PRO.initFeatureIcons();
  }

  /* ---------------------------------------------------------------------
   * 13) SUPPRESS THIRD-PARTY PURCHASE WIDGETS/HEADINGS (scoped, performant)
   * ------------------------------------------------------------------- */
  function scrubExternalBits(){
    var killSel = [
      '.seal-subscriptions-wrapper',
      '.sls-widget',
      '.sls-purchase-options-title',
      '[data-sls="purchase-options-title"]',
      '.sls-purchase-options-container',
      '.seal-purchase-options-container',
      '.sls-option-container',
      '.sls-subscription-details',
      '.seal-subscription-details',
      '[data-sls="subscription-details"]',
      '.sls-more-info-container'
    ].join(',');
    var junk = ROOT.querySelectorAll(killSel);
    for (var i=0;i<junk.length;i++) junk[i].style.setProperty('display','none','important');

    // Hide stray headings “Purchase options”
    var hs = ROOT.querySelectorAll('h1,h2,h3,h4,p,div,section');
    for (var j=0;j<hs.length;j++){
      var t = (hs[j].textContent||'').trim().toLowerCase();
      if (t === 'purchase options') hs[j].style.setProperty('display','none','important');
    }
  }
  scrubExternalBits();

  // Observe only within ROOT; disconnect on section unload
  var scrubber = new MutationObserver(scrubExternalBits);
  scrubber.observe(ROOT, {childList:true,subtree:true});
  document.addEventListener('shopify:section:unload', function(){ scrubber.disconnect(); }, {once:true});

  /* NEW: if the features block mutates (apps re-render), re-apply icons */
  var feats = wrap.querySelector('.sub-features');
  if (feats) {
    new MutationObserver(function(){
      if (window.ET_PRO && typeof window.ET_PRO.initFeatureIcons === 'function') {
        window.ET_PRO.initFeatureIcons();
      }
    }).observe(feats, {childList:true, subtree:true});
  }

  /* ---------------------------------------------------------------------
   * 14) KEEP HEIGHT CORRECT ON MUTATIONS/RESIZE (throttled)
   * ------------------------------------------------------------------- */
  if (bodyEl){
    new MutationObserver(function(){ adjustBodyHeight(); })
      .observe(bodyEl, {childList:true, subtree:true});
  }
  var resizeT = null;
  window.addEventListener('resize', function(){
    clearTimeout(resizeT);
    resizeT = setTimeout(adjustBodyHeight, 120);
  });
})();

/* ===========================================================
 * FEATURE ICONS — ultra-safe swapper (no observers, no loops)
 * Replaces the first 3 SVGs under the AutoDelivery card, in order:
 *   0 = calendar, 1 = pause, 2 = check
 * Honors data attribute on #pro-product:
 *   data-icon-style="solid" | "outline"   (defaults to solid)
 * =========================================================== */
(function () {
  try {
    window.ET_PRO = window.ET_PRO || {};
    var NS = window.ET_PRO;

    // guard against re-definition
    if (NS.initFeatureIcons) return;

    var ICONS = {
      calendar: {
        solid:
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">' +
            '<path d="M6.75 3a.75.75 0 0 1 .75.75V5h9V3.75a.75.75 0 0 1 1.5 0V5h.75A2.25 2.25 0 0 1 21 7.25v10.5A2.25 2.25 0 0 1 18.75 20H5.25A2.25 2.25 0 0 1 3 17.75V7.25A2.25 2.25 0 0 1 5.25 5h.75V3.75A.75.75 0 0 1 6.75 3Zm-.75 7.5v7.25h12V10.5H6Z" fill="currentColor"/>' +
          '</svg>',
        outline:
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">' +
            '<g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
              '<rect x="3.5" y="5.5" width="17" height="15" rx="2.5"/>' +
              '<path d="M7 2.75V6M17 2.75V6M3.5 10.5h17"/>' +
            '</g>' +
          '</svg>'
      },
      pause: {
        solid:
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">' +
            '<path d="M8.25 5.25A1.25 1.25 0 0 1 9.5 4h1a1.25 1.25 0 0 1 1.25 1.25v13.5A1.25 1.25 0 0 1 10.5 20h-1a1.25 1.25 0 0 1-1.25-1.25V5.25Zm6 0A1.25 1.25 0 0 1 15.5 4h1a1.25 1.25 0 0 1 1.25 1.25v13.5A1.25 1.25 0 0 1 16.5 20h-1a1.25 1.25 0 0 1-1.25-1.25V5.25Z" fill="currentColor"/>' +
          '</svg>',
        outline:
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">' +
            '<g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
              '<rect x="7.5" y="4.5" width="3" height="15" rx="1.5"/>' +
              '<rect x="13.5" y="4.5" width="3" height="15" rx="1.5"/>' +
            '</g>' +
          '</svg>'
      },
      check: {
        solid:
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">' +
            '<path d="M9.53 16.47 4.8 11.74a1.1 1.1 0 0 1 0-1.56l.88-.88a1.1 1.1 0 0 1 1.56 0l2.82 2.82 6.7-6.7a1.1 1.1 0 0 1 1.56 0l.88.88a1.1 1.1 0 0 1 0 1.56l-8.26 8.26a1.1 1.1 0 0 1-1.56 0Z" fill="currentColor"/>' +
          '</svg>',
        outline:
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">' +
            '<path d="M5 12.5 9.5 17 19 7.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg>'
      }
    };

    function getRoot() { return document.getElementById('pro-product'); }
    function getStyle() {
      var r = getRoot();
      var v = (r && r.getAttribute('data-icon-style')) || 'solid';
      v = (v + '').toLowerCase();
      return v.indexOf('outline') > -1 ? 'outline' : 'solid';
    }

    // Replace the first three svgs under the subscribe card, whatever they are
    NS.initFeatureIcons = function () {
      try {
        var root = getRoot(); if (!root) return;

        var style = getStyle();
        var svgs = root.querySelectorAll('[data-subscribe-card] .sub-feature .sub-ico svg, [data-subscribe-card] .sub-feature svg');
        if (!svgs || !svgs.length) return;

        var htmls = [
          ICONS.calendar[style],
          ICONS.pause[style],
          ICONS.check[style]
        ];

        for (var i = 0; i < svgs.length && i < 3; i++) {
          if (svgs[i] && htmls[i]) {
            // Use outerHTML to replace the node even if the app owns innerHTML
            svgs[i].outerHTML = htmls[i];
          }
        }

        // breadcrumb for debugging
        try { document.documentElement.setAttribute('data-et-icon-style', style); } catch (e) {}
      } catch (err) {
        try { console.warn('initFeatureIcons failed:', err); } catch (_e) {}
      }
    };
  } catch (e) {
    try { console.warn('initFeatureIcons bootstrap failed:', e); } catch (_e) {}
  }


})();



// Product Recommendations with Shopify API
(function(){

    // Create shimmer placeholder HTML
  function createShimmerPlaceholder(count = 5) {
    const shimmerItems = Array(count).fill(0).map(() => `
      <div class="productgrid--item shimmer-loading">
        <div class="shimmer-card">
          <div class="shimmer-image"></div>
          <div class="shimmer-content">
            <div class="shimmer-line shimmer-line--price"></div>
            <div class="shimmer-line shimmer-line--title"></div>
            <div class="shimmer-line shimmer-line--vendor"></div>
            <div class="shimmer-line shimmer-line--description"></div>
            <div class="shimmer-line shimmer-line--description short"></div>
            <div class="shimmer-button"></div>
          </div>
        </div>
      </div>
    `).join('');

    return shimmerItems;
  }

  // Show loading shimmer
  function showLoading(container) {
    container.innerHTML = createShimmerPlaceholder(CONFIG.limit);
    container.classList.add('is-loading');
  }



// Main initialization
  async function init() {
    const container = document.querySelector('[data-product-recommendations]');
    



    showLoading(container);

    
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }


});
